import { useEffect, useRef, useContext } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

interface UseNavigationBlockerOptions {
  /**
   * Whether to block navigation
   */
  shouldBlock: boolean;
  /**
   * Callback fired when navigation is blocked
   */
  onNavigationBlocked: () => void;
  /**
   * Whether to also block browser navigation (refresh, close tab, etc.)
   * @default true
   */
  blockBrowserNavigation?: boolean;
}

interface UseNavigationBlockerReturn {
  /**
   * Call this to confirm and proceed with the pending navigation
   */
  confirmNavigation: () => void;
  /**
   * Call this to cancel the pending navigation
   */
  cancelNavigation: () => void;
}

/**
 * Hook to block React Router navigation and optionally browser navigation
 * Shows a custom dialog/modal when navigation is attempted
 *
 * @example
 * const { confirmNavigation, cancelNavigation } = useNavigationBlocker({
 *   shouldBlock: hasUnsavedChanges,
 *   onNavigationBlocked: () => setShowWarningDialog(true),
 * });
 */
export function useNavigationBlocker({
  shouldBlock,
  onNavigationBlocked,
  blockBrowserNavigation = true,
}: UseNavigationBlockerOptions): UseNavigationBlockerReturn {
  const navigationContext = useContext(UNSAFE_NavigationContext);
  const pendingNavigationRef = useRef<{
    type: 'push' | 'replace';
    args: any[];
  } | null>(null);
  const isNavigatingRef = useRef(false);

  // Intercept React Router navigation
  useEffect(() => {
    if (!navigationContext || !shouldBlock) return undefined;

    const { navigator } = navigationContext;
    const originalPush = navigator.push.bind(navigator);
    const originalReplace = navigator.replace.bind(navigator);

    navigator.push = (...args: any[]) => {
      if (shouldBlock && !isNavigatingRef.current) {
        // Store the navigation attempt
        pendingNavigationRef.current = { type: 'push', args };
        // Trigger the callback
        onNavigationBlocked();
      } else {
        originalPush(...(args as [any]));
      }
    };

    navigator.replace = (...args: any[]) => {
      if (shouldBlock && !isNavigatingRef.current) {
        // Store the navigation attempt
        pendingNavigationRef.current = { type: 'replace', args };
        // Trigger the callback
        onNavigationBlocked();
      } else {
        originalReplace(...(args as [any]));
      }
    };

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
    };
  }, [shouldBlock, navigationContext, onNavigationBlocked]);

  // Handle browser navigation (refresh, close, back button)
  useEffect(() => {
    if (!blockBrowserNavigation || !shouldBlock) return undefined;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldBlock) {
        e.preventDefault();
        // eslint-disable-next-line no-param-reassign
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldBlock, blockBrowserNavigation]);

  const confirmNavigation = () => {
    // Proceed with the pending navigation
    if (pendingNavigationRef.current && navigationContext) {
      const { type, args } = pendingNavigationRef.current;

      // Set flag to bypass the navigation block
      isNavigatingRef.current = true;

      // Navigate immediately - the isNavigatingRef flag prevents re-interception
      if (type === 'push') {
        navigationContext.navigator.push(...(args as [any]));
      } else {
        navigationContext.navigator.replace(...(args as [any]));
      }

      // Clean up
      pendingNavigationRef.current = null;
      isNavigatingRef.current = false;
    }
  };

  const cancelNavigation = () => {
    // Clear pending navigation
    pendingNavigationRef.current = null;
  };

  return {
    confirmNavigation,
    cancelNavigation,
  };
}
